import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../src/hooks/useFocusTrap';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Fondo del backdrop. by default oscuro con blur. */
  backdropClassName?: string;
  /**
   * `id` del elemento que da nombre al diálogo. Si no se pasa, se toma
   * automáticamente el primer encabezado que haya dentro (ver abajo).
   */
  labelledBy?: string;
  /** Nombre accesible cuando el diálogo no tiene un encabezado visible. */
  label?: string;
}

let activeModalCount = 0;

/**
 * Modal base: se monta en document.body (evita quedar tapado por el header
 * u otros contenedores con su propio stacking context), cierra con click
 * fuera del contenido y con la tecla Escape.
 *
 * ## Accesibilidad (arreglo de UX_AUDIT H-02)
 *
 * Este componente no tenía semántica de diálogo. En números: 14 archivos lo
 * montan, y en todo the frontend había 2 `role="dialog"` y 3 `aria-modal`. El
 * efecto para un usuario real era este:
 *
 * - Al abrirse, el foco se quedaba en la página de debajo. Con Tab se recorría
 *   el contenido tapado, invisible.
 * - Un lector de pantalla no anunciaba que se hubiera abierto nada y seguía
 *   leyendo la página anterior como si el modal no existiera.
 * - Al cerrar, el foco no volvía al botón que lo abrió: se perdía al principio
 *   del documento. En un flujo de confirmación destructiva, eso significa
 *   quedarse sin saber si la acción ocurrió.
 *
 * Se arregla **aquí**, no en los 14 sitios: cualquier modal existente o futuro
 * hereda el comportamiento correcto sin cambiar una línea en su archivo. La
 * trampa de foco es `useFocusTrap`, que ya existía en el repositorio y cuyo
 * propio comentario apuntaba a este componente como su siguiente adoptante.
 *
 * El `role`/`aria-modal` viven en el contenedor a pantalla completa, que es el
 * elemento que envuelve al contenido del diálogo: así no hace falta insertar un
 * nodo intermedio y la composición de los 14 usos actuales no se altera.
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  backdropClassName,
  labelledBy,
  label,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoLabelId, setAutoLabelId] = useState<string | undefined>();

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);

    activeModalCount++;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      activeModalCount = Math.max(0, activeModalCount - 1);
      if (activeModalCount === 0) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, onClose]);

  // Nombre accesible automático: casi todos los modales del proyecto ya pintan
  // un encabezado visible con su título. instead of pedir que 14 archivos pasen
  // un `labelledBy` -- y confiar en que el siguiente se acuerde -- se toma ese
  // encabezado y se le presta su `id`. Un diálogo sin nombre se anuncia solo
  // como "diálogo", que no dice nada de en qué se está a punto de intervenir.
  useLayoutEffect(() => {
    if (!isOpen || labelledBy || label) return;
    const heading = containerRef.current?.querySelector<HTMLElement>('h1, h2, h3, h4');
    if (!heading) return;
    if (!heading.id) heading.id = `modal-title-${Math.random().toString(36).slice(2, 9)}`;
    setAutoLabelId(heading.id);
  }, [isOpen, labelledBy, label]);

  useFocusTrap(containerRef, isOpen);

  if (!isOpen) return null;

  const resolvedLabelledBy = labelledBy ?? autoLabelId;

  return createPortal(
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={resolvedLabelledBy}
      aria-label={!resolvedLabelledBy ? (label ?? 'Diálogo') : undefined}
      className={backdropClassName ?? 'fixed inset-0 z-[1000] p-4 sm:p-6 bg-black/50 backdrop-blur-sm overflow-y-auto custom-scrollbar'}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Centering lives on this inner wrapper, never on the scrollable backdrop itself: a
          flex container that both centers (`items-center`) and scrolls (`overflow-y-auto`)
          clips whatever sticks out above the centered content, with no way to scroll up to
          see it (the classic centered-overlay bug). `min-h-full` keeps content centered when
          it's shorter than the viewport, and lets the backdrop scroll normally end-to-end
          when it's taller. */}
      <div className="min-h-full flex items-center justify-center">
        {children}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
